import { FC, useEffect } from "react";

import { useBlocNext } from "@blac/react";
import styled from "@emotion/styled";
import MealInsightsCubit from "src/state/MealInsightsCubit/MealInsightsCubit";
import MealsStatsCubit, {
  calorieTargetExceeded
} from "src/state/MealsStatsCubit/MealsStatsCubit";
import { LoadingIndicator } from "src/ui/components/Loader/Loader";

import MealTodayEmptyCard from "./MealTodayEmptyCard";
import MealTodayInsightsCard from "./MealTodayInsightsCard";
import MealTodayLockedCard from "./MealTodayLockedCard";
import MealTodayStats from "./MealTodayStats";
import { dailyInsightState } from "./mealInsightHelpers";
import RxDietCard from "./RxDietCard";
import { useRefreshOnAppVisible } from "./useRefreshOnAppVisible";

const Overview = styled.div`
  position: relative;
  isolation: isolate;
  display: flex;
  flex-direction: column;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const LoadingWrap = styled.div`
  position: relative;
  min-height: 220px;
`;

interface MealTodayOverviewProps {
  /**
   * Opens the meal photo picker, shared with the page's FAB. Omitted on desktop,
   * where meal upload isn't offered.
   */
  onAddMeal?: () => void;
  /** Forwarded to the empty card, which shows a QR to the app on desktop. */
  variant?: "mobile" | "desktop";
}

const MealTodayOverview: FC<MealTodayOverviewProps> = ({
  onAddMeal,
  variant = "mobile"
}) => {
  const [
    {
      todaySummary,
      hasAnyMeals,
      initialLoadComplete,
      loading: historyLoading,
      error: historyError
    }
  ] = useBlocNext(MealInsightsCubit);
  const [
    { loaded, insights, calories, loading: statsLoading, error: statsError },
    { load, refresh }
  ] = useBlocNext(MealsStatsCubit);

  useEffect(() => {
    if (!loaded) {
      void load();
    }
  }, [loaded, load]);

  useRefreshOnAppVisible(refresh);

  // After a meal is saved both cubits are invalidated and refetch. Until they
  // settle, todaySummary/insights still hold the stale pre-upload values, so
  // rendering now would briefly flash the "no meal today" card before the real
  // content appears. Hold a spinner until the refreshed data lands — falling
  // through on error so a failed load doesn't spin forever.
  const historyPending =
    (!initialLoadComplete || historyLoading) && !historyError;
  const statsPending = (!loaded || statsLoading) && !statsError;

  if (historyPending || statsPending) {
    return (
      <LoadingWrap>
        <LoadingIndicator />
      </LoadingWrap>
    );
  }

  const insightState = dailyInsightState({
    hasAnyMeals,
    insights,
    mealCount: todaySummary.mealCount
  });

  if (insightState === "intro") {
    return <MealTodayEmptyCard onAddMeal={onAddMeal} variant={variant} />;
  }

  const overCalorieTarget = calorieTargetExceeded(
    calories.total,
    calories.target
  );

  return (
    <Stack>
      <Overview>
        {insightState === "unlocked" ? (
          <MealTodayInsightsCard />
        ) : (
          <MealTodayLockedCard mealCount={todaySummary.mealCount} />
        )}
        <MealTodayStats />
      </Overview>
      {!overCalorieTarget && <RxDietCard />}
    </Stack>
  );
};

export default MealTodayOverview;
